import { createSelector } from "reselect";
import { SaviSelectIntlCarSegmentDetails, SaviSelectIntlCombinations, SaviSelectIntlHotelSegmentDetails, SaviSelectIntlSegmentDetails, SelectedCombinations } from "../../../models/itinerary";
import { AppState } from "../../model";
import moment from "moment";
import deepmerge from "deepmerge";

/* -------------------------------------------------------------------------- */
/*                        SaviSelectIntlCombinations                          */
/* -------------------------------------------------------------------------- */

export const getCombinationsForSaviSelectIntl = (state: AppState): SaviSelectIntlCombinations =>
  state.itinerary.data.combinations;

export const getFlightCombinationsForSaviSelectIntl = createSelector(
  getCombinationsForSaviSelectIntl,
  combinations => combinations.Flight
);

export const getHotelCombinationsForSaviSelectIntl = createSelector(
  getCombinationsForSaviSelectIntl,
  combinations => combinations.Hotel
);

export const getCarCombinationsForSaviSelectIntl = createSelector(
  getCombinationsForSaviSelectIntl,
  combinations => combinations.Car
);

export const getRecommendRateWithinSegmentForSaviSelectIntl = (segmentId: number) => {
  return createSelector(
    getHotelCombinationsForSaviSelectIntl,
    hotelCombinations => hotelCombinations.find(segment => segment.type0Id === segmentId),
  );
};

export const getRecommendCarWithinSegmentForSaviSelectIntl = (segmentId: number) => {
  return createSelector(
    getCarCombinationsForSaviSelectIntl,
    carCombinations => carCombinations.find(segment => segment.type0Id === segmentId),
  );
};

export const hasMultipleRatesWithinSegmentForSaviSelectIntl = (segmentId: number, hotelId: number) => {
  return createSelector(
    getHotelCombinationsForSaviSelectIntl,
    hotelCombinations =>
      hotelCombinations.filter(hotel => hotel.type0Id === segmentId).length > 1 ||
      hotelCombinations.find(hotel => hotel.type0Id === segmentId && hotel.landId === hotelId)!.segments.length > 1,
  );
};

export const hasMultipleCarsWithinSegmentForSaviSelectIntl = (segmentId: number) => {
  return createSelector(
    getCarCombinationsForSaviSelectIntl,
    carCombinations => carCombinations.filter(car => car.type0Id === segmentId).length > 1,
  );
};

export const getSegmentTypeForSaviSelectIntl = (segmentId: number) => {
  return createSelector(
    getHotelCombinationsForSaviSelectIntl,
    getCarCombinationsForSaviSelectIntl,
    (hotelCombinations, carCombinations) => {
      const hotel = hotelCombinations.find(hotel => hotel.type0Id === segmentId);
      if(hotel) return "Hotel";

      const car = carCombinations.find(car => car.type0Id === segmentId);
      if(car) return "Car";

      throw new Error("Segment not found");
    }
  );
}

export const getLandSegmentIdsForSaviSelectIntl = createSelector(
  getHotelCombinationsForSaviSelectIntl,
  getCarCombinationsForSaviSelectIntl,
    (hotelCombinations, carCombinations) => {
    let landSegmentIds : Set<number> = new Set();
    hotelCombinations.forEach(hotel => {
      landSegmentIds.add(hotel.type0Id);
    });
    carCombinations.forEach(car => {
      landSegmentIds.add(car.type0Id);
    });

    return Array.from(landSegmentIds);
  }
);

export const getLandIdsWithinSegmentForSaviSelectIntl = (segmentId: number) =>
  createSelector(
    getHotelCombinationsForSaviSelectIntl,
    getCarCombinationsForSaviSelectIntl,
    (hotelCombinations, carCombinations) => {
      let type0Ids: string[] = [];
      type0Ids = hotelCombinations.filter(hotel => hotel.type0Id === segmentId).map(hotel => hotel.landId.toString());
      if(type0Ids.length > 0) { return type0Ids; }

      type0Ids = carCombinations.filter(car => car.type0Id === segmentId).map(car => car.landId.toString());
      return type0Ids;
    }
  );

/* -------------------------------------------------------------------------- */
/*                        SaviSelectIntlSegmentDetails                        */
/* -------------------------------------------------------------------------- */

export const getSegmentDetailsForSaviSelectIntl = (state: AppState): SaviSelectIntlSegmentDetails =>
  state.itinerary.data.segmentDetails;

export const getAirSegmentDetailsForSaviSelectIntl = createSelector(
  getSegmentDetailsForSaviSelectIntl,
  segmentDetail=> segmentDetail.airSegmentDetails,
);

export const getHotelSegmentDetailsForSaviSelectIntl = createSelector(
  getSegmentDetailsForSaviSelectIntl,
  segmentDetail=> segmentDetail.hotelSegmentDetails,
);

export const getCarSegmentDetailsForSaviSelectIntl = createSelector(
  getSegmentDetailsForSaviSelectIntl,
  segmentDetail=> segmentDetail.carSegmentDetails,
);

export const getASegmentDetailsForSaviSelectIntlHotel = (segmentId: number) => {
  return createSelector(
    getHotelSegmentDetailsForSaviSelectIntl,
    hotelSegmentDetails => hotelSegmentDetails.find(hotel => hotel.type0Id === segmentId),
  );
};

export const getAHotelDetailsForSaviSelectIntl = (segmentId: number, hotelId: number) => {
  return createSelector(
    getHotelSegmentDetailsForSaviSelectIntl,
    hotelSegmentDetails => hotelSegmentDetails.find(hotel => hotel.type0Id === segmentId && hotel.hotelId === hotelId),
  );
};

export const getASegmentDetailsForSaviSelectIntlCar = (segmentId: number) => {
  return createSelector(
    getCarSegmentDetailsForSaviSelectIntl,
    carSegmentDetails => carSegmentDetails.find(car => car.type0Id === segmentId),
  );
};

export const getACarDetailsForSaviSelectIntl = (segmentId: number, carId: number) => {
  return createSelector(
    getCarSegmentDetailsForSaviSelectIntl,
    carSegmentDetails => carSegmentDetails.find(car => car.type0Id === segmentId && car.carId === carId),
  );
};

export const getCarDetailsForSaviSelectIntl = (segmentId: number, carId: number) => {
  return createSelector(
    getCarSegmentDetailsForSaviSelectIntl,
    carSegmentDetails => carSegmentDetails.filter(car => car.type0Id === segmentId && car.carId === carId),
  );
};


/* -------------------------------------------------------------------------- */
/*                             SelectedCombinations                           */
/* -------------------------------------------------------------------------- */

export const getSelectedCombinationsForSaviSelectIntl = (state: AppState): SelectedCombinations =>
  state.itinerary.data.selectedCombinations;

export const getSelectedHotelCombinationsForSaviSelectIntl = createSelector(
  getSelectedCombinationsForSaviSelectIntl,
  selectedCombinations => selectedCombinations.selectedHotelCombinations
);

export const getSelectedCarCombinationsForSaviSelectIntl = createSelector(
  getSelectedCombinationsForSaviSelectIntl,
  selectedCombinations => selectedCombinations.selectedCarCombinations
);

export const getSelectedHotelSegmentCombinationForSaviSelectIntl  = (segmentId: number) => {
  return createSelector(
    getSelectedHotelCombinationsForSaviSelectIntl,
    selectedCombinations => selectedCombinations.find(hotel => hotel.type0Id == segmentId)
  );
};

export const getSelectedCarSegmentCombinationForSaviSelectIntl  = (segmentId: number) => {
  return createSelector(
    getSelectedCarCombinationsForSaviSelectIntl,
    selectedCombinations => selectedCombinations.find(car => car.type0Id == segmentId)
  );
};

/* -------------------------------------------------------------------------- */
/*                        Combine Combinations and Details                    */
/* -------------------------------------------------------------------------- */

export const groupSegmentsByDayForSaviSelectIntl = (
  landSegmentIds: number[],
  hotelSegmentDetails: SaviSelectIntlHotelSegmentDetails[],
  carSegmentDetails : SaviSelectIntlCarSegmentDetails[]
) => {
  const getDate = (saviLandSegmentId: number): Date => {
    const hotel = hotelSegmentDetails.find((hotel) => hotel.type0Id === saviLandSegmentId);
    if(hotel) {
      return hotel.checkInDateAndTime;
    }

    // If hotel is not found, then it must be a car
    const car = carSegmentDetails.find((car) => car.type0Id === saviLandSegmentId);
    if(car) {
      return car.pickupDateAndTime;
    }

    return new Date();
  };

  const getFormattedDate = (saviLandSegmentId: number): string => {
    return moment(getDate(saviLandSegmentId)).format('YYYY-MM-DD');
  };

  return landSegmentIds.reduce(
    (previous: Record<string, number[]>, current: number) => {
      return deepmerge(previous, {
        [getFormattedDate(current)]: [current],
      });
    },
    {},
  );
};

export const getLandSegmentsGroupedByDayForSaviSelectIntl = createSelector(
  getLandSegmentIdsForSaviSelectIntl,
  getHotelSegmentDetailsForSaviSelectIntl,
  getCarSegmentDetailsForSaviSelectIntl,
  groupSegmentsByDayForSaviSelectIntl,
);

export const getSegmentRatesDetailsForSaviSelectIntl = (segmentId: number, hotelId: number) => {
  return createSelector(
    getHotelCombinationsForSaviSelectIntl,
    getHotelSegmentDetailsForSaviSelectIntl,
    (hotelCombinations, hotelSegmentDetails) => {
      let ratesDetails : SaviSelectIntlHotelSegmentDetails[] = [];
      const filterHotelCombination = hotelCombinations.filter(hotel => hotel.type0Id === segmentId && hotel.landId === hotelId);
      filterHotelCombination.forEach(combination => {
        combination.segments.forEach((segment) => {
          const filteredRate = hotelSegmentDetails.find(hotelDetail => hotelDetail.type0Id === combination.type0Id && hotelDetail.hotelId === combination.landId && segment.id === hotelDetail.roomId);
          if(filteredRate) {
            ratesDetails.push(filteredRate);
          }
        })
      });

      return ratesDetails;
    }
  );
}